import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { AppDispatch, RootState } from "../../../../redux/store";
import { listenToShortstayData, upsertShortstay } from "../../../../redux/shortstaysSlice";
import {
  Lock,
  Key,
  UserCircle,
  Handshake,
  NotePencil,
  CheckCircle,
} from "phosphor-react";
import { Lockers } from "@phosphor-icons/react";

const options = [
  {
    key: "smartLock",
    label: "Smart lock",
    description: "Guests use a code or app to open a wifi-connected lock.",
    icon: <Lock size={26} />,
  },
  {
    key: "keypad",
    label: "Keypad",
    description: "Guests use a code you provide to open an electronic lock.",
    icon: <Key size={26} />,
  },
  {
    key: "lockbox",
    label: "Lockbox",
    description: "Guests use a code to open a safe with a key inside.",
    icon: <Lockers size={26} />,
  },
  {
    key: "buildingStaff",
    label: "Building staff",
    description: "Staff is available 24/7 to let guests in.",
    icon: <UserCircle size={26} />,
  },
  {
    key: "inPerson",
    label: "In-person greeting",
    description: "Guests meet you or co-host to pick up keys.",
    icon: <Handshake size={26} />,
  },
  {
    key: "other",
    label: "Other",
    description: "Guests use a method not listed here.",
    icon: <NotePencil size={26} />,
  },
];

const T = {
  bg:       "#FFFFFF",
  text:     "#0A0A0A",
  textSoft: "#444444",
  muted:    "#9A9A9A",
  border:   "#EBEBEB",
  gold:     "#C9A84C",
  goldDark: "#9A7A2E",
  black:    "#0A0A0A",
  sans:     "'Montserrat', sans-serif",
};

const ShortCheckinMethod: React.FC = () => {
  const { listingId } = useParams<{ listingId: string }>();
  const dispatch = useDispatch<AppDispatch>();

  const currentListing = useSelector((s: RootState) => s.shortstays.currentListing);
  const [selectedKeys,    setSelectedKeys]    = useState<string[]>([]);
  const [descriptions,    setDescriptions]    = useState<Record<string, string>>({});
  const [activeKey,       setActiveKey]       = useState<string | null>(null);
  const [tempDescription, setTempDescription] = useState("");
  const [showModal,       setShowModal]       = useState(false);
  const [saving,          setSaving]          = useState(false);
  const [saved,           setSaved]           = useState(false);

  useEffect(() => {
    if (listingId) dispatch(listenToShortstayData(listingId));
  }, [dispatch, listingId]);

  useEffect(() => {
    const methods = currentListing?.checkInMethods || {};
    setSelectedKeys(Object.keys(methods));
    setDescriptions(methods);
  }, [currentListing]);

  const toggleOption = (key: string) => {
    setActiveKey(key);
    setTempDescription(descriptions[key] || "");
    setShowModal(true);
  };

  const saveDescription = () => {
    if (!activeKey) return;
    const updatedDescriptions = { ...descriptions, [activeKey]: tempDescription.trim() };
    const updatedKeys = Object.keys(updatedDescriptions).filter(k => updatedDescriptions[k]);
    setDescriptions(updatedDescriptions);
    setSelectedKeys(updatedKeys);
    setShowModal(false);
  };

  const handleSave = async () => {
    if (!listingId) return;
    setSaving(true);
    await dispatch(upsertShortstay({ listingId, data: { checkInMethods: descriptions } }));
    setSaving(false);
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  };

  return (
    <div style={{ fontFamily: T.sans, color: T.text, maxWidth: 700, margin: "0 auto", position: "relative" }}>

      {/* ── SAVING OVERLAY ── */}
      {saving && (
        <div style={{
          position: "fixed", inset: 0,
          background: "rgba(255,255,255,0.65)",
          backdropFilter: "blur(6px)",
          zIndex: 1100,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 20,
        }}>
          {/* spinning ring */}
          <div style={{
            width: 56, height: 56,
            borderRadius: "50%",
            border: `3px solid ${T.border}`,
            borderTop: `3px solid ${T.black}`,
            animation: "spin 0.8s linear infinite",
          }} />
          <p style={{
            fontSize: 12, fontWeight: 500,
            color: T.black, letterSpacing: "0.08em",
            textTransform: "uppercase", margin: 0,
          }}>
            Saving
          </p>
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
      )}

      {/* ── SUCCESS TOAST ── */}
      {saved && (
        <div style={{
          position: "fixed",
          bottom: 32, left: "50%",
          transform: "translateX(-50%)",
          zIndex: 1200,
          background: "#FFFFFF",
          border: `1px solid #D4EDDA`,
          borderRadius: 12,
          padding: "14px 22px",
          display: "flex",
          alignItems: "center",
          gap: 10,
          boxShadow: "0 8px 30px rgba(0,0,0,0.12), 0 2px 8px rgba(0,0,0,0.06)",
          minWidth: 220,
        }}>
          <CheckCircle size={20} weight="fill" color="#22883F" />
          <div>
            <p style={{ fontSize: 12, fontWeight: 600, color: "#22883F", margin: 0 }}>
              Successfully saved
            </p>
            <p style={{ fontSize: 11, color: T.muted, margin: "2px 0 0" }}>
              Check-in methods updated
            </p>
          </div>
        </div>
      )}

      {/* heading */}
      <div style={{ marginBottom: 28 }}>
        <p style={{
          fontSize: 9, color: T.muted, letterSpacing: "0.13em",
          textTransform: "uppercase", margin: "0 0 6px",
        }}>
          Guest experience
        </p>
        <h2 style={{ fontSize: 20, fontWeight: 500, color: T.black, margin: 0 }}>
          Check-in methods
        </h2>
        <p style={{ fontSize: 12, color: T.muted, marginTop: 6 }}>
          Select how guests will access your property.
        </p>
      </div>

      {/* grid */}
      <div style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
        gap: 16,
        marginBottom: 32,
      }}>
        {options.map(opt => {
          const active = selectedKeys.includes(opt.key);
          return (
            <div
              key={opt.key}
              onClick={() => toggleOption(opt.key)}
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 14,
                padding: "20px 18px",
                borderRadius: 14,
                cursor: "pointer",
                transition: "all .2s ease",
                background: active ? T.black : "#FFFFFF",
                border: active ? `1.5px solid ${T.black}` : `1px solid ${T.border}`,
                boxShadow: active
                  ? "0 8px 24px rgba(0,0,0,0.18), 0 2px 6px rgba(0,0,0,0.12), inset 0 1px 0 rgba(255,255,255,0.06)"
                  : "0 4px 14px rgba(0,0,0,0.07), 0 1px 3px rgba(0,0,0,0.05)",
                transform: active ? "translateY(-2px)" : "translateY(0)",
                minHeight: 150,
                position: "relative",
              }}
            >
              {/* icon circle */}
              <div style={{
                width: 48,
                height: 48,
                borderRadius: "50%",
                background: active ? "rgba(201,168,76,0.15)" : "#F5F5F5",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: active ? T.gold : T.black,
                flexShrink: 0,
                transition: "all .2s ease",
              }}>
                {opt.icon}
              </div>

              <div style={{ flex: 1 }}>
                <p style={{
                  fontSize: 13, fontWeight: 600,
                  color: active ? "#FFFFFF" : T.black,
                  margin: "0 0 5px",
                  transition: "color .2s",
                }}>
                  {opt.label}
                </p>
                <p style={{
                  fontSize: 11,
                  color: active ? "rgba(255,255,255,0.55)" : T.muted,
                  margin: 0,
                  lineHeight: 1.5,
                  transition: "color .2s",
                }}>
                  {opt.description}
                </p>
                {descriptions[opt.key] && (
                  <p style={{
                    fontSize: 11,
                    color: active ? T.gold : T.goldDark,
                    margin: "8px 0 0",
                    fontStyle: "italic",
                    lineHeight: 1.4,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    display: "-webkit-box",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical",
                  }}>
                    {descriptions[opt.key]}
                  </p>
                )}
              </div>

              {/* selected dot */}
              {active && (
                <span style={{
                  position: "absolute",
                  top: 14, right: 14,
                  width: 7, height: 7,
                  borderRadius: "50%",
                  background: T.gold,
                  boxShadow: `0 0 8px ${T.gold}`,
                }} />
              )}
            </div>
          );
        })}
      </div>

      {/* save */}
      <div style={{ display: "flex", justifyContent: "flex-end" }}>
        <button
          onClick={handleSave}
          disabled={saving}
          style={{
            background: saving ? "#F0F0F0" : T.black,
            color: saving ? T.muted : "#FFFFFF",
            border: "none",
            borderRadius: 10,
            padding: "11px 28px",
            fontSize: 12,
            fontWeight: 600,
            fontFamily: T.sans,
            cursor: saving ? "not-allowed" : "pointer",
            letterSpacing: "0.04em",
            transition: "all .2s ease",
            boxShadow: saving ? "none" : "0 4px 14px rgba(0,0,0,0.18)",
          }}
        >
          Save changes
        </button>
      </div>

      {/* modal */}
      {showModal && (
        <div style={{
          position: "fixed", inset: 0,
          background: "rgba(0,0,0,0.35)",
          backdropFilter: "blur(4px)",
          zIndex: 999,
          display: "flex", alignItems: "center", justifyContent: "center",
          padding: 20,
        }}>
          <div style={{
            background: "#FFFFFF",
            borderRadius: 16,
            padding: 28,
            width: "100%",
            maxWidth: 420,
            boxShadow: "0 20px 60px rgba(0,0,0,0.2), 0 4px 16px rgba(0,0,0,0.1)",
            border: `1px solid ${T.border}`,
          }}>
            <p style={{
              fontSize: 9, color: T.muted, letterSpacing: "0.13em",
              textTransform: "uppercase", margin: "0 0 6px",
            }}>
              Check-in detail
            </p>
            <h3 style={{ fontSize: 15, fontWeight: 500, color: T.black, margin: "0 0 4px" }}>
              {options.find(o => o.key === activeKey)?.label}
            </h3>
            <p style={{ fontSize: 11, color: T.muted, margin: "0 0 18px" }}>
              {options.find(o => o.key === activeKey)?.description}
            </p>

            <textarea
              placeholder="e.g., Enter code 1234 on the keypad at the front door."
              value={tempDescription}
              onChange={e => setTempDescription(e.target.value)}
              style={{
                width: "100%",
                height: 110,
                padding: "12px 14px",
                borderRadius: 10,
                border: `1px solid ${T.border}`,
                background: "#FAFAFA",
                color: T.black,
                fontFamily: T.sans,
                fontSize: 13,
                resize: "none",
                outline: "none",
                marginBottom: 18,
                boxSizing: "border-box",
                lineHeight: 1.6,
              }}
            />

            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <button
                onClick={() => setShowModal(false)}
                style={{
                  background: "transparent",
                  border: `1px solid ${T.border}`,
                  borderRadius: 8,
                  padding: "9px 18px",
                  fontSize: 12,
                  fontFamily: T.sans,
                  color: T.textSoft,
                  cursor: "pointer",
                  fontWeight: 500,
                }}
              >
                Cancel
              </button>
              <button
                onClick={saveDescription}
                style={{
                  background: T.black,
                  border: "none",
                  borderRadius: 8,
                  padding: "9px 20px",
                  fontSize: 12,
                  fontFamily: T.sans,
                  color: "#FFFFFF",
                  cursor: "pointer",
                  fontWeight: 600,
                  boxShadow: "0 4px 12px rgba(0,0,0,0.18)",
                }}
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ShortCheckinMethod;